import { FunctionComponent, useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Stack,
  Alert,
  Collapse,
  Divider,
  Badge
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  CloudSync,
  Error as ErrorIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Storage as StorageIcon,
  Edit as EditIcon
} from '@mui/icons-material'
import { mutationQueue, type OfflineMutation } from '../services/offline/mutationQueue'
import { formPersistence, type FormDraft } from '../services/offline/formPersistence'
import { useOfflineQueueProcessor } from '../hooks/useOfflineMutation'

interface OfflineStatusProps {}

const OfflineStatus: FunctionComponent<OfflineStatusProps> = () => {
  const [expanded, setExpanded] = useState(false)
  const [mutations, setMutations] = useState<OfflineMutation[]>([])
  const [drafts, setDrafts] = useState<FormDraft[]>([])
  const [loading, setLoading] = useState(false)
  const { processQueue } = useOfflineQueueProcessor()

  const loadData = async () => {
    try {
      setLoading(true)
      const [allMutations, allDrafts] = await Promise.all([
        mutationQueue.getPendingMutations().then(async () => {
          // Get all mutations (pending, failed, completed)
          await mutationQueue.init()
          const [pending, failed] = await Promise.all([
            mutationQueue.getPendingMutations(),
            mutationQueue.getFailedMutations()
          ])
          return [...pending, ...failed]
        }),
        Promise.resolve(formPersistence.getAllDrafts())
      ])

      // Sort by timestamp (newest first)
      setMutations(allMutations.sort((a, b) => b.timestamp - a.timestamp))
      setDrafts(allDrafts.sort((a, b) => b.timestamp - a.timestamp))
    } catch (error) {
      console.error('Failed to load offline data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (expanded) {
      loadData()
    }
  }, [expanded])

  // Auto-refresh when expanded
  useEffect(() => {
    if (!expanded) return

    const interval = setInterval(loadData, 2000)
    return () => clearInterval(interval)
  }, [expanded])

  const handleRetryMutation = async (mutationId: string) => {
    try {
      await mutationQueue.updateMutationStatus(mutationId, 'pending')
      await processQueue()
      loadData()
    } catch (error) {
      console.error('Failed to retry mutation:', error)
    }
  }

  const handleClearMutation = async (mutationId: string) => {
    try {
      await mutationQueue.removeMutation(mutationId)
      loadData()
    } catch (error) {
      console.error('Failed to clear mutation:', error)
    }
  }

  const handleClearDraft = (draftKey: string) => {
    formPersistence.clear(draftKey)
    loadData()
  }

  const handleManualSync = async () => {
    try {
      setLoading(true)
      await processQueue()
      await loadData()
    } catch (error) {
      console.error('Manual sync failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClearCompleted = async () => {
    try {
      await mutationQueue.clearCompleted()
      loadData()
    } catch (error) {
      console.error('Failed to clear completed:', error)
    }
  }

  const getMutationIcon = (status: OfflineMutation['status']) => {
    switch (status) {
      case 'pending':
        return <PendingIcon color='warning' />
      case 'processing':
        return <CloudSync color='info' className='rotating' />
      case 'completed':
        return <CheckCircleIcon color='success' />
      case 'failed':
        return <ErrorIcon color='error' />
    }
  }

  const getMutationTypeLabel = (type: OfflineMutation['mutationType']) => {
    const labels = {
      protocol: 'Protokół',
      furniture: 'Meble',
      animal: 'Zwierzęta',
      key: 'Klucze',
      meter: 'Liczniki',
      room: 'Pokoje',
      item: 'Przedmioty',
      protocolHouseholdAppliances: 'Sprzęt AGD',
      protocolFurnitures: 'Meble (Nowe)',
      sign: 'Podpis'
    }
    return labels[type] || type
  }

  const getFormTypeLabel = (type: FormDraft['formType']) => {
    const labels: Record<string, string> = {
      protocol: 'Protokół',
      furniture: 'Meble',
      animals: 'Zwierzęta',
      keys: 'Klucze',
      meters: 'Liczniki',
      rooms: 'Pokoje',
      'control-questions': 'Pytania kontrolne'
    }
    return labels[type] || type
  }

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / (1000 * 60))

    if (minutes < 1) return 'przed chwilą'
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} godz.`
    const days = Math.floor(hours / 24)
    return `${days} dni`
  }

  const pendingCount = mutations.filter((m) => m.status === 'pending').length
  const failedCount = mutations.filter((m) => m.status === 'failed').length
  const processingCount = mutations.filter((m) => m.status === 'processing').length

  if (mutations.length === 0 && drafts.length === 0 && !loading) {
    return null
  }

  return (
    <Card sx={{ mx: 1.5, mb: 1 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center'>
            <StorageIcon color='primary' />
            <Typography variant='subtitle2' fontWeight={600}>
              Stan offline
            </Typography>

            <Stack direction='row' spacing={1}>
              {pendingCount > 0 && (
                <Badge badgeContent={pendingCount} color='warning'>
                  <Chip
                    label='Kolejka'
                    size='small'
                    color='warning'
                    sx={{ fontSize: '11px', height: '22px' }}
                  />
                </Badge>
              )}

              {failedCount > 0 && (
                <Badge badgeContent={failedCount} color='error'>
                  <Chip
                    label='Błędy'
                    size='small'
                    color='error'
                    sx={{ fontSize: '11px', height: '22px' }}
                  />
                </Badge>
              )}

              {processingCount > 0 && (
                <Chip
                  label='Sync...'
                  size='small'
                  color='info'
                  sx={{ fontSize: '11px', height: '22px' }}
                />
              )}

              {drafts.length > 0 && (
                <Badge badgeContent={drafts.length} color='info'>
                  <Chip
                    label='Drafty'
                    size='small'
                    color='info'
                    sx={{ fontSize: '11px', height: '22px' }}
                  />
                </Badge>
              )}
            </Stack>
          </Stack>

          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {/* Action Buttons */}
            <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
              <Button
                size='small'
                variant='outlined'
                startIcon={<RefreshIcon />}
                onClick={handleManualSync}
                disabled={loading}
              >
                Synchronizuj
              </Button>

              <Button
                size='small'
                variant='outlined'
                startIcon={<ClearIcon />}
                onClick={handleClearCompleted}
                disabled={loading}
              >
                Wyczyść ukończone
              </Button>
            </Stack>

            {/* Mutations Queue */}
            {mutations.length > 0 && (
              <>
                <Typography variant='subtitle2' gutterBottom>
                  Kolejka operacji ({mutations.length})
                </Typography>

                <List dense sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                  {mutations.map((mutation) => (
                    <ListItem key={mutation.id} divider>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getMutationIcon(mutation.status)}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Stack direction='row' spacing={1} alignItems='center'>
                            <Chip
                              label={getMutationTypeLabel(mutation.mutationType)}
                              size='small'
                              variant='outlined'
                              sx={{ fontSize: '10px', height: '20px' }}
                            />
                            <Typography variant='caption' color='text.secondary'>
                              {mutation.method} • {formatTimestamp(mutation.timestamp)}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Box>
                            <Typography variant='caption' color='text.secondary' noWrap>
                              {mutation.endpoint}
                            </Typography>
                            {mutation.errorMessage && (
                              <Alert severity='error' sx={{ mt: 0.5, fontSize: '11px' }}>
                                {mutation.errorMessage}
                              </Alert>
                            )}
                            {mutation.retryCount > 0 && (
                              <Typography variant='caption' color='text.secondary'>
                                Ponowiono: {mutation.retryCount}/{mutation.maxRetries}
                              </Typography>
                            )}
                          </Box>
                        }
                      />

                      <ListItemSecondaryAction>
                        <Stack direction='row' spacing={0.5}>
                          {mutation.status === 'failed' && (
                            <IconButton
                              edge='end'
                              size='small'
                              onClick={() => handleRetryMutation(mutation.id)}
                              title='Ponów próbę'
                            >
                              <RefreshIcon fontSize='small' />
                            </IconButton>
                          )}

                          {(mutation.status === 'failed' || mutation.status === 'completed') && (
                            <IconButton
                              edge='end'
                              size='small'
                              onClick={() => handleClearMutation(mutation.id)}
                              title='Usuń'
                            >
                              <ClearIcon fontSize='small' />
                            </IconButton>
                          )}
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Form Drafts */}
            {drafts.length > 0 && (
              <>
                {mutations.length > 0 && <Divider sx={{ my: 2 }} />}

                <Typography variant='subtitle2' gutterBottom>
                  Robocze wersje ({drafts.length})
                </Typography>

                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {drafts.map((draft) => (
                    <ListItem key={draft.id} divider>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <EditIcon color='info' />
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Stack direction='row' spacing={1} alignItems='center'>
                            <Chip
                              label={getFormTypeLabel(draft.formType)}
                              size='small'
                              color='info'
                              sx={{ fontSize: '10px', height: '20px' }}
                            />
                            <Typography variant='caption' color='text.secondary'>
                              Protokół #{draft.protocolId}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Typography variant='caption' color='text.secondary'>
                            Zapisano: {formatTimestamp(draft.timestamp)}
                          </Typography>
                        }
                      />

                      <ListItemSecondaryAction>
                        <IconButton
                          edge='end'
                          size='small'
                          onClick={() => handleClearDraft(draft.id)}
                          title='Usuń draft'
                        >
                          <ClearIcon fontSize='small' />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {mutations.length === 0 && drafts.length === 0 && (
              <Alert severity='success' sx={{ mt: 1 }}>
                Wszystkie operacje zsynchronizowane, brak roboczych wersji
              </Alert>
            )}
          </Box>
        </Collapse>
      </CardContent>

      <style>
        {`
          .rotating {
            animation: rotate 1s linear infinite;
          }
          
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Card>
  )
}

export default OfflineStatus
